/**
 * Organisation → Close organisation (owners only). Closing the account and
 * deleting its data is done by FinderlyFix staff, not instantly: the owner
 * sends a request (`POST /partner/org/close-request`, the organisation's name
 * typed back), staff confirm by email, pause the widget, offer an export and
 * delete within 30 days. `GET /partner/org/close-request` shows a request
 * already sent, and the staff offboarding case once one is open.
 *
 * Other roles see a one-line note. An API without the route (404) reads as
 * "no request yet".
 */
import { useId, useState } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import type { ApiError } from '../api/errors';
import type { CloseRequestState } from '../api/types';
import { Link } from '../router';
import { ErrorNotice, Field, Modal, formatDate, humanize } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

const CONTACT_EMAIL = 'partners@finderlyfix.com';
const REASON_MAX = 1000;

export function CloseOrganisation() {
  const { api, org, role } = useSession();
  const owner = can(role, 'closeOrg');
  const state = useQuery<CloseRequestState | null>(
    () =>
      api.getCloseRequest().catch((err: ApiError) => {
        if (err.status === 404) return null;
        throw err;
      }),
    ['close-request', org?.id],
    { enabled: owner },
  );
  const [open, setOpen] = useState(false);
  const contact = state.data?.contact_email ?? CONTACT_EMAIL;
  const request = state.data?.request ?? null;
  const offboarding = state.data?.offboarding ?? null;

  return (
    <section className="panel panel-pad stack-sm" aria-labelledby="close-org-h">
      <h2 id="close-org-h" className="h-sub">Close organisation</h2>
      <p className="small" style={{ margin: 0 }}>
        FinderlyFix staff close the account and delete its data; it doesn’t happen instantly. Sending a request tells them. They
        confirm with you by email, pause your widget and widget pages, offer you an export of your data, then delete leads,
        contractors, destinations, sites, settings and media within 30 days of the request.
      </p>
      <p className="small muted" style={{ margin: 0 }}>
        Kept: the audit log, Partner Terms acceptances, and the organisation’s name as a record. Projects a homeowner continued in
        the FinderlyFix app stay with the homeowner.
      </p>
      <p className="small muted" style={{ margin: 0 }}>
        To only stop the widget, pause it on <Link to="/sites">Sites &amp; keys</Link> instead. Questions:{' '}
        <a href={`mailto:${contact}`}>{contact}</a>.
      </p>
      {!owner ? (
        <p className="small" style={{ margin: 0 }}>Only an owner can ask to close the organisation.</p>
      ) : state.loading && !state.data ? null : request ? (
        <div className="notice info small" role="status" data-testid="close-request-status">
          <div className="stack-sm" style={{ gap: 4 }}>
            <strong>Requested on {formatDate(request.requested_at)}. FinderlyFix will email you to confirm.</strong>
            {offboarding ? (
              <span>
                Status: {humanize(offboarding.state)}
                {offboarding.deadline_at ? ` · data deleted by ${formatDate(offboarding.deadline_at)}` : ''}
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <ErrorNotice error={state.error} />
          <div>
            <button type="button" className="btn btn-danger-subtle btn-sm" onClick={() => setOpen(true)}>
              Ask to close organisation…
            </button>
          </div>
        </>
      )}
      {open ? (
        <CloseDialog
          name={org?.display_name ?? ''}
          onClose={() => setOpen(false)}
          onDone={(next) => {
            state.setData({ request: next, offboarding, contact_email: contact });
            setOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}

function CloseDialog({
  name,
  onClose,
  onDone,
}: {
  name: string;
  onClose: () => void;
  onDone: (request: NonNullable<CloseRequestState['request']>) => void;
}) {
  const { api } = useSession();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const nameId = useId();
  const send = useMutation(() => api.requestClose(typed.trim(), reason.trim() || undefined));
  const nameOk = typed.trim().toLowerCase() === name.trim().toLowerCase() && name.trim() !== '';

  const submit = async () => {
    const res = await send.run();
    if (res) onDone(res.request);
  };

  return (
    <Modal
      open
      title="Ask FinderlyFix to close this organisation?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={send.pending}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={() => void submit()} disabled={send.pending || !nameOk}>
            {send.pending ? 'Sending…' : 'Send request'}
          </button>
        </>
      }
    >
      <div className="stack-sm">
        <p style={{ margin: 0 }}>
          Nothing changes yet. FinderlyFix will email you to confirm before pausing your widget, and will offer you an export of your
          data before anything is deleted.
        </p>
        <div className="field">
          <label htmlFor={nameId}>
            Type <strong>{name}</strong> to confirm
          </label>
          <input
            id={nameId}
            className="input"
            value={typed}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && nameOk) void submit();
            }}
            autoFocus
          />
        </div>
        <Field label="Reason (optional)" hint="Helps us improve. Only FinderlyFix staff see it.">
          {(p) => (
            <textarea {...p} className="input" rows={3} maxLength={REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
        </Field>
        <ErrorNotice error={send.error} />
      </div>
    </Modal>
  );
}
